import { CloseSquare } from "iconsax-react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { RxMedicationName } from "../../MedicationName";

export const RxItemPage = ({ rx, onClose }) => {
  if (!rx) {
    toast.error("This rx does not exist");
    setTimeout(() => {
      onClose();
    }, 100);
    return;
  }

  const hasFormulations =
    Array.isArray(rx?.rx_formulations) && rx.rx_formulations.length > 0;

  return (
    <>
      <div className="fixed top-0 right-0 h-[100vh] w-[450px] bg-white px-3 py-10 overflow-y-scroll z-30">
        <div className="flex items-center justify-between">
          <div className="text-[18px] font-medium select-none">
            RX Information - #{rx.id}
          </div>
          <button onClick={() => onClose()}>
            <CloseSquare variant="Bold" />
          </button>
        </div>

        {/* Primary Overview Section */}
        <div className="border rounded-md px-3 py-3 mt-5 grid grid-cols-2 items-start gap-x-5 gap-y-5">
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Pharmacy:
            </h2>
            <p className="text-xs capitalize">
              {rx.pharmacy?.pharmacy_name || "--"}
            </p>
          </div>
          {rx.order?.order_method?.toLowerCase() === "patient" && (
            <div>
              <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
                Patient:
              </h2>
              <Link
                to={`/dashboard/patients/${rx.patient?.id}`}
                className="inline-block underline text-primary text-xs capitalize"
              >
                {rx.patient?.lastname + " " + rx.patient?.firstname}
              </Link>
            </div>
          )}

          {!hasFormulations && (
            <>
              <div>
                <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                  RX Number:
                </h2>
                <span className="text-xs">{rx.id || "--"}</span>
              </div>
              <div>
                <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                  Medication Name:
                </h2>
                <span className="text-xs capitalize">
                  <RxMedicationName rx={rx} />
                </span>
              </div>
              <div>
                <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                  Strength:
                </h2>
                <span className="text-xs">{rx.strength || "--"}</span>
              </div>
              <div>
                <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                  Quantity Dispensed:
                </h2>
                <span className="text-xs capitalize">
                  {rx.quantity_dispensed
                    ? parseInt(rx.quantity_dispensed, 10)
                    : "--"}
                </span>
              </div>
            </>
          )}

          <div>
            <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
              Product Type:
            </h2>
            <span className="text-xs capitalize">
              {rx.product_type || "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
              Category:
            </h2>
            <span className="text-xs">{rx.category || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
              Refill Remaining:
            </h2>
            <span className="text-xs">{rx.refill_remaining || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
              Dispensed At:
            </h2>
            <span className="text-xs capitalize">
              {rx.dispensed === "true"
                ? new Date(rx.dispensed_at).toDateString()
                : "--"}
            </span>
          </div>
        </div>

        {/* Monthly Formulations Section (Middle) */}
        {hasFormulations && (
          <div className="mt-6">
            <h2 className="mb-3 font-medium text-[16px] text-gray-800">
              Monthly Medications
            </h2>
            <div className="space-y-4">
              {rx.rx_formulations.map((item, index) => (
                <div
                  key={item.id || index}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 shadow-sm"
                >
                  <h3 className="font-medium text-sm text-primary mb-3 capitalize">
                    {item.direction_label || `Formulation #${index + 1}`}
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-7">
                    <div>
                      <h4 className="text-sm text-[black] mb-1.5">
                        Medication Name:
                      </h4>
                      <p className="text-xs capitalize text-gray-800">
                        {item.formulation_name || "--"}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm text-[black] mb-1.5">Strength:</h4>
                      <p className="text-xs text-gray-800 font-medium">
                        {item.formulation_strength || "--"}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm text-[black] mb-1.5">
                        Quantity Dispensed:
                      </h4>
                      <p className="text-xs text-gray-800 font-medium capitalize">
                        {item.quantity_to_dispense
                          ? parseInt(item.quantity_to_dispense, 10)
                          : "--"}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm text-[black] mb-1.5">
                        RX Number:
                      </h4>
                      <p className="text-xs text-gray-800 font-medium">
                        {item.medication_rx_id || "--"}
                      </p>
                    </div>
                  </div>
                  {item.direction_text && (
                    <div className="mt-3 bg-white py-1 px-2 rounded-md border border-gray-200">
                      <span className="text-xs font-medium text-[black]">
                        SIG:{" "}
                      </span>
                      <span className="text-xs text-gray-800 capitalize">
                        {item.direction_text}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Details Section (Bottom) */}
        <div className="mt-6">
          <h2 className="mb-3 font-medium text-[16px] text-gray-800">
            Additional Details
          </h2>
          <div className="border rounded-md px-3 py-3 grid grid-cols-2 items-start gap-x-5 gap-y-5">
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Fill ID:
              </h2>
              <span className="text-xs">{rx.fill_id || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Form:
              </h2>
              <span className="text-xs">{rx.form || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Size:
              </h2>
              <span className="text-xs">{rx.size || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                mG/ML:
              </h2>
              <span className="text-xs">{rx.mg || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Device:
              </h2>
              <span className="text-xs">{rx.device || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Route of Admin:
              </h2>
              <span className="text-xs">{rx.route_of_admin || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Package:
              </h2>
              <span className="text-xs">{rx.package || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Units:
              </h2>
              <span className="text-xs">{rx.units || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Packages Dispensed:
              </h2>
              <span className="text-xs">{rx.packages_dispensed || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Refill Schedule:
              </h2>
              <span className="text-xs">{rx.refill_schedule || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                RX Serial Number:
              </h2>
              <span className="text-xs">{rx.serial_number || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                RX Serial Number State:
              </h2>
              <span className="text-xs">{rx.serial_number_state || "--"}</span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                RX Effective Date:
              </h2>
              <span className="text-xs">
                {rx.effective_date
                  ? new Date(rx.effective_date).toDateString()
                  : "--"}
              </span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                RX Expiration Date:
              </h2>
              <span className="text-xs">
                {rx.expiration_date
                  ? new Date(rx.expiration_date).toDateString()
                  : "--"}
              </span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Created on:
              </h2>
              <span className="text-xs">
                {rx.created_at ? new Date(rx.created_at).toDateString() : "--"}
              </span>
            </div>
            <div>
              <h2 className="mb-1 capitalize text-[black] font-[500] text-sm">
                Last Updated:
              </h2>
              <span className="text-xs capitalize">
                {rx.updated_at ? new Date(rx.updated_at).toDateString() : "--"}
              </span>
            </div>
          </div>
        </div>

        {!hasFormulations && rx.direction && (
          <div className="mt-6">
            <h2 className="mb-3 font-medium">Direction</h2>
            <div className="border-gray-200 border px-5 py-5 rounded-lg">
              <span className="text-xs">{rx.direction}</span>
            </div>
          </div>
        )}
        {rx.special_instructions && (
          <div className="mt-6">
            <h2 className="mb-3 font-medium">Special Instructions</h2>
            <div className="border-gray-200 border px-5 py-5 rounded-lg">
              <span className="text-xs">{rx.special_instructions}</span>
            </div>
          </div>
        )}
      </div>
      <div
        className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
        onClick={() => onClose()}
      />
    </>
  );
};
